import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiFetch } from '../lib/api';
import { TenantAdd } from './TenantAdd';

interface Tenant {
  slug: string;
  displayName: string;
  replyTo?: string;
  status: string;
  createdAt: string;
}

const STATUS: Record<string, string> = {
  active: 'bg-emerald-50 text-emerald-700 ring-1 ring-emerald-600/20',
  disabled: 'bg-zinc-100 text-zinc-500 ring-1 ring-zinc-500/20',
};

function statusClass(s: string) {
  return STATUS[s] ?? 'bg-amber-50 text-amber-700 ring-1 ring-amber-600/20';
}

function fmtDate(iso: string) {
  try {
    return new Date(iso).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  } catch {
    return iso;
  }
}

export function Tenants() {
  const queryClient = useQueryClient();
  const tenantsQuery = useQuery({
    queryKey: ['tenants'],
    queryFn: async () => {
      const data = await apiFetch('/tenants');
      return (data.items ?? []) as Tenant[];
    },
  });
  const createTenant = useMutation({
    mutationFn: async (input: { slug: string; displayName: string; replyTo?: string }) =>
      apiFetch('/tenants', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(input),
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['tenants'] });
    },
  });

  const tenants = tenantsQuery.data ?? [];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold text-zinc-900">Tenants</h1>
          <p className="mt-1 text-sm text-zinc-500">Sender identities and DNS verification status.</p>
        </div>
        <TenantAdd onCreate={async (input) => createTenant.mutateAsync(input)} />
      </div>

      {createTenant.isSuccess ? (
        <div className="flex items-start gap-3 rounded-xl bg-emerald-50 px-4 py-3 text-sm text-emerald-800 ring-1 ring-emerald-200">
          <svg viewBox="0 0 16 16" fill="currentColor" className="mt-0.5 h-4 w-4 shrink-0 text-emerald-500">
            <path fillRule="evenodd" d="M8 15A7 7 0 108 1a7 7 0 000 14zm3.844-8.791a.75.75 0 00-1.188-.918l-3.7 4.79-1.646-1.647a.75.75 0 00-1.06 1.061l2.25 2.25a.75.75 0 001.124-.073l4.22-5.463z" clipRule="evenodd" />
          </svg>
          Tenant created — DNS verification usually completes within 15 minutes.
        </div>
      ) : null}

      <div className="overflow-hidden rounded-xl border border-zinc-200 bg-white shadow-sm">
        <table className="min-w-full">
          <thead>
            <tr className="border-b border-zinc-100 bg-zinc-50/50">
              <th className="px-5 py-3 text-left text-xs font-medium uppercase tracking-wider text-zinc-400">Slug</th>
              <th className="px-5 py-3 text-left text-xs font-medium uppercase tracking-wider text-zinc-400">Display Name</th>
              <th className="px-5 py-3 text-left text-xs font-medium uppercase tracking-wider text-zinc-400">Status</th>
              <th className="px-5 py-3 text-left text-xs font-medium uppercase tracking-wider text-zinc-400">Created</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-100">
            {tenants.map((t) => (
              <tr key={t.slug} className="group hover:bg-zinc-50/70 transition-colors">
                <td className="px-5 py-3.5">
                  <Link
                    to={`/tenants/${t.slug}`}
                    className="font-mono text-sm font-medium text-indigo-600 hover:text-indigo-800 hover:underline underline-offset-2"
                  >
                    {t.slug}
                  </Link>
                </td>
                <td className="px-5 py-3.5 text-sm text-zinc-700">{t.displayName}</td>
                <td className="px-5 py-3.5">
                  <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${statusClass(t.status)}`}>
                    {t.status}
                  </span>
                </td>
                <td className="px-5 py-3.5 text-sm tabular-nums text-zinc-400">
                  <time dateTime={t.createdAt}>{fmtDate(t.createdAt)}</time>
                </td>
              </tr>
            ))}
            {!tenants.length && !tenantsQuery.isLoading ? (
              <tr>
                <td colSpan={4} className="px-5 py-12 text-center">
                  <p className="text-sm text-zinc-400">No tenants yet.</p>
                  <p className="mt-1 text-xs text-zinc-300">Add a tenant to create a sending identity.</p>
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>
    </div>
  );
}
